import jsPDF from 'jspdf';
import autoTable, { applyPlugin } from 'jspdf-autotable';
import PAID_STAMP_PNG_BASE64 from './paidStampImage';
import { parseInvoiceDate } from './invoiceHelpers';

// YYYY-MM-DD strings are parsed as UTC midnight by `new Date`, which rolls
// back one day in negative-offset timezones. This helper preserves the
// intended calendar day for date-only strings.
const formatLocaleDate = (value) => {
  const d = parseInvoiceDate(value);
  return d ? d.toLocaleDateString() : 'N/A';
};

// Apply the autoTable plugin to jsPDF
applyPlugin(jsPDF);

// Helper function to safely call autoTable
const safeAutoTable = (doc, options) => {
  // Direct function call (preferred method)
  if (typeof autoTable === 'function') {
    return autoTable(doc, options);
  }
  // Fallback to plugin method on doc instance
  if (doc && typeof doc.autoTable === 'function') {
    return doc.autoTable(options);
  }
  console.error('autoTable not available - check jspdf-autotable installation');
  throw new Error('PDF table generation not available');
};

// Professional Muted Color Palette - Corporate/Business Look
const COLORS = {
  primary: [51, 65, 85],         // Slate-700 (Dark blue-gray)
  primaryLight: [100, 116, 139], // Slate-500
  primaryDark: [30, 41, 59],     // Slate-800
  secondary: [71, 85, 105],      // Slate-600
  accent: [107, 114, 128],       // Gray-500
  success: [22, 101, 52],        // Green-800 (Muted green)
  danger: [153, 27, 27],         // Red-800 (Muted red)
  warning: [146, 64, 14],        // Amber-800 (Muted amber)
  info: [30, 64, 175],           // Blue-800
  gray: [107, 114, 128],         // Gray-500
  darkGray: [55, 65, 81],        // Gray-700
  mediumGray: [156, 163, 175],   // Gray-400
  lightGray: [243, 244, 246],    // Gray-100
  borderGray: [229, 231, 235],   // Gray-200
  white: [255, 255, 255],
  black: [17, 24, 39]            // Gray-900
};

// Best-effort read of the tenant's company info saved on the Settings page.
// Used to brand every generated report (header + logo + generated date).
const getStoredCompanyInfo = () => {
  try {
    const raw = typeof window !== 'undefined'
      ? window.localStorage.getItem('maidly_settings')
      : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed?.companyInfo || {};
  } catch (e) {
    return {};
  }
};

// Branded report header — replaces the tiny grey "MAIDLY BUSINESS MANAGER"
// line with the tenant's real company name (+ logo, address, email if set).
// Accent bar + generated date on the right keep it visually anchored.
const REPORT_ACCENT = [36, 156, 219];

const addProfessionalHeader = (doc, title, subtitle = '', companyOverride = null) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const company = companyOverride || getStoredCompanyInfo();
  const displayName = (company.name || '').trim();

  // Top accent bar (matches invoice/estimate PDFs)
  doc.setFillColor(...REPORT_ACCENT);
  doc.rect(0, 0, pageWidth, 4, 'F');

  // Logo (top-left, if the workspace has uploaded one)
  let contentX = 14;
  if (company.logo) {
    try {
      const fmt = /^data:image\/(\w+);/i.exec(company.logo)?.[1]?.toUpperCase() || 'PNG';
      doc.addImage(company.logo, fmt, 14, 10, 22, 22);
      contentX = 40;
    } catch (e) {
      // Fallback silently — bad logo shouldn't block the report.
    }
  }

  // Company name — bold, prominent (only shown when the tenant configured one)
  if (displayName) {
    doc.setTextColor(...COLORS.black);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(displayName, contentX, 18);
  }

  // Optional address / email — one compact grey line under the name
  const contactBits = [company.address, company.email, company.phone].filter(Boolean);
  if (contactBits.length) {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    const contactLine = contactBits.join('  •  ');
    doc.text(contactLine, contentX, displayName ? 24 : 18);
  }

  // Generated date — right side
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text(
    `Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
    pageWidth - 14,
    18,
    { align: 'right' },
  );

  // Report title (large, left) — anchor for the whole document
  doc.setTextColor(...COLORS.black);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 14, 44);

  // Subtitle / date range
  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    doc.text(subtitle, 14, 51);
  }

  // Separator
  doc.setDrawColor(...COLORS.borderGray);
  doc.setLineWidth(0.5);
  doc.line(14, 56, pageWidth - 14, 56);

  doc.setTextColor(...COLORS.black);
  return 64; // Return Y position after header
};

// Professional footer - disabled/empty
const addProfessionalFooter = (doc, brandText = '') => {
  // Footer removed for cleaner look
  return;
};

// Summary card — subtle top-tint stripe for visual variety without noise.
const addSummaryCard = (doc, yPos, label, value, color = COLORS.darkGray, width = 55) => {
  const x = doc.lastCardX || 14;
  const height = 32;

  // Card body
  doc.setFillColor(...COLORS.white);
  doc.setDrawColor(...COLORS.borderGray);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, yPos, width, height, 2, 2, 'FD');

  // Left accent stripe — thin colored bar for hierarchy
  doc.setFillColor(...color);
  doc.roundedRect(x, yPos, 2, height, 1, 1, 'F');

  // Label (top, muted)
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.gray);
  doc.text(String(label).toUpperCase(), x + 8, yPos + 9);

  // Value (large, bold, dark)
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.black);
  doc.text(String(value), x + 8, yPos + 22);

  doc.lastCardX = x + width + 6;
  return yPos + height + 8;
};

// Reset card position for new row
const resetCardRow = (doc) => {
  doc.lastCardX = 14;
};

/**
 * Fits an arbitrary number of summary cards evenly across the printable
 * width (A4 minus 14mm side margins). Guarantees every report has the
 * same tight, page-fitted card row without hand-tuning widths.
 *
 * `cards` is `[{ label, value, color }]`. Returns the y position after
 * the row so callers can chain sections.
 */
const layoutSummaryRow = (doc, yPos, cards = []) => {
  if (!cards.length) return yPos;
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const gap = 5;
  const usable = pageWidth - margin * 2;
  const width = (usable - gap * (cards.length - 1)) / cards.length;

  cards.forEach((c, i) => {
    doc.lastCardX = margin + (width + gap) * i;
    addSummaryCard(doc, yPos, c.label, c.value, c.color || COLORS.darkGray, width);
  });
  resetCardRow(doc);
  return yPos + 46;
};

// Section title with subtle underline
const addSectionTitle = (doc, yPos, title) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.darkGray);
  doc.text(title.toUpperCase(), 14, yPos);
  
  // Subtle underline
  doc.setDrawColor(...COLORS.borderGray);
  doc.setLineWidth(0.5);
  doc.line(14, yPos + 3, pageWidth - 14, yPos + 3);
  
  doc.setTextColor(...COLORS.black);
  return yPos + 12;
};

// Info row
const addInfoRow = (doc, yPos, label, value, pageWidth) => {
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.gray);
  doc.text(label + ':', 20, yPos);
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.black);
  doc.text(String(value || 'N/A'), 70, yPos);
  
  return yPos + 8;
};

// Professional table styles - clean and corporate
const getTableStyles = () => ({
  theme: 'plain',
  styles: {
    fontSize: 9,
    cellPadding: 5,
    lineColor: COLORS.borderGray,
    lineWidth: 0.3,
    textColor: COLORS.darkGray,
  },
  headStyles: {
    fillColor: COLORS.lightGray,
    textColor: COLORS.darkGray,
    fontStyle: 'bold',
    fontSize: 8,
    cellPadding: 6,
  },
  alternateRowStyles: {
    fillColor: [250, 250, 252],
  },
  columnStyles: {},
  margin: { left: 14, right: 14 },
});

// jsPDF's default Helvetica is WinAnsi-only, so bundled category emojis
// (📦, 🍽️, ✈️…) render as garbled glyphs. Strip them for PDF output.
const stripEmoji = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// ==================== EXPENSE REPORT ====================
export const generateExpenseReportPDF = (reportData, dateRange = {}) => {
  const doc = new jsPDF();

  const subtitle = dateRange.start && dateRange.end
    ? `${dateRange.start} — ${dateRange.end}`
    : 'All Time';

  let yPos = addProfessionalHeader(doc, 'Expense Report', subtitle);

  // Field-name compat: backend returns snake_case (`tax_deductible`,
  // `payment_method`), while some callers pass camelCase — read both.
  const expenses = reportData.expenses || [];
  const totalAmount = Number(reportData.totalAmount)
    || expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const expenseCount = expenses.length;
  const avgExpense = expenseCount > 0 ? totalAmount / expenseCount : 0;
  const taxDeductible = Number(reportData.taxDeductible)
    || expenses
      .filter((e) => e.tax_deductible ?? e.taxDeductible)
      .reduce((sum, e) => sum + Number(e.amount || 0), 0);

  // Summary Cards — auto-fitted to the printable width.
  yPos = layoutSummaryRow(doc, yPos, [
    { label: 'Total Expenses', value: `$${totalAmount.toFixed(2)}`, color: COLORS.info },
    { label: 'Transactions',   value: expenseCount,                 color: COLORS.primaryLight },
    { label: 'Average',        value: `$${avgExpense.toFixed(2)}`,  color: COLORS.warning },
    { label: 'Tax Deductible', value: `$${taxDeductible.toFixed(2)}`, color: COLORS.success },
  ]);

  // Category breakdown — accepts either `byCategory` (backend) or `categories`.
  const categories = reportData.byCategory || reportData.categories || {};
  if (categories && Object.keys(categories).length > 0) {
    yPos = addSectionTitle(doc, yPos, 'By Category');

    const categoryData = Object.entries(categories)
      .sort((a, b) => Number(b[1]) - Number(a[1]))
      .map(([cat, amount]) => [
        stripEmoji(cat) || 'Uncategorized',
        `$${Number(amount).toFixed(2)}`,
        totalAmount > 0 ? `${((Number(amount) / totalAmount) * 100).toFixed(1)}%` : '—',
      ]);

    safeAutoTable(doc, {
      ...getTableStyles(),
      startY: yPos,
      head: [['Category', 'Amount', 'Percentage']],
      body: categoryData,
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
      },
    });

    yPos = doc.lastAutoTable.finalY + 15;
  }

  // Expense details
  if (expenses.length > 0) {
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }

    yPos = addSectionTitle(doc, yPos, 'Expense Details');

    const expenseData = expenses.map((expense) => [
      formatLocaleDate(expense.date),
      expense.merchant || expense.description || 'N/A',
      stripEmoji(expense.category) || 'Uncategorized',
      `$${Number(expense.amount || 0).toFixed(2)}`,
      expense.payment_method || expense.paymentMethod || 'N/A',
      (expense.tax_deductible ?? expense.taxDeductible) ? 'Yes' : '—',
    ]);

    safeAutoTable(doc, {
      ...getTableStyles(),
      startY: yPos,
      head: [['Date', 'Merchant', 'Category', 'Amount', 'Payment', 'Tax']],
      body: expenseData,
      columnStyles: {
        3: { halign: 'right', fontStyle: 'bold' },
        5: { halign: 'center' },
      },
    });
  } else {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...COLORS.gray);
    doc.text('No expenses recorded for this period.', 14, yPos + 6);
  }

  addProfessionalFooter(doc);
  doc.save(`Expense_Report_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ==================== TAX & FINANCIAL REPORT ====================
/**
 * Comprehensive tax + P&L + quarterly estimates PDF for the Reports & Taxes
 * page. Consumes the same shapes produced by `ReportsTaxes.js`
 * (`taxSummary`, `profitLoss`, `quarterlyEstimates`) so no data re-shaping
 * is required on the caller side.
 */
export const generateTaxReportPDF = ({
  taxSummary,
  profitLoss,
  quarterlyEstimates,
  scheduleCLabels = {},
  dateRange = {},
} = {}) => {
  const doc = new jsPDF();

  const subtitle = dateRange.start && dateRange.end
    ? `${dateRange.start} — ${dateRange.end}`
    : 'All Time';

  let yPos = addProfessionalHeader(doc, 'Tax & Financial Report', subtitle);

  // ---- Top summary strip: Revenue • Expenses • Net Profit • Tax Deductible
  const revenue = Number(profitLoss?.totalRevenue || 0);
  const expenses = Number(profitLoss?.totalExpenses || 0);
  const mileageDed = Number(profitLoss?.mileageDeduction || 0);
  const netProfit = Number(profitLoss?.netProfit ?? (revenue - expenses - mileageDed));
  const totalDeductible = Number(taxSummary?.totalDeductible || 0);

  yPos = layoutSummaryRow(doc, yPos, [
    { label: 'Revenue',        value: `$${revenue.toFixed(2)}`,        color: COLORS.info },
    { label: 'Expenses',       value: `$${expenses.toFixed(2)}`,       color: COLORS.warning },
    { label: 'Net Profit',     value: `$${netProfit.toFixed(2)}`,      color: netProfit >= 0 ? COLORS.success : COLORS.danger },
    { label: 'Tax Deductible', value: `$${totalDeductible.toFixed(2)}`, color: COLORS.primaryLight },
  ]);

  // ---- Schedule C Breakdown
  if (taxSummary?.byCategory && Object.keys(taxSummary.byCategory).length > 0) {
    yPos = addSectionTitle(doc, yPos, 'Schedule C Deductions');

    const rows = Object.entries(taxSummary.byCategory)
      .sort((a, b) => Number(b[1]?.deductible || 0) - Number(a[1]?.deductible || 0))
      .map(([key, data]) => [
        scheduleCLabels[key] || key,
        `$${Number(data?.total || 0).toFixed(2)}`,
        `$${Number(data?.deductible || 0).toFixed(2)}`,
      ]);

    safeAutoTable(doc, {
      ...getTableStyles(),
      startY: yPos,
      head: [['Category', 'Total Spent', 'Deductible']],
      body: rows,
      foot: [[
        'TOTAL',
        `$${Number(taxSummary.totalExpenses || 0).toFixed(2)}`,
        `$${Number(taxSummary.totalDeductible || 0).toFixed(2)}`,
      ]],
      footStyles: { fillColor: COLORS.lightGray, textColor: COLORS.black, fontStyle: 'bold' },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right', fontStyle: 'bold' },
      },
    });
    yPos = doc.lastAutoTable.finalY + 6;

    // Mileage supplemental line
    if (Number(taxSummary.totalMiles) > 0) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...COLORS.gray);
      doc.text(
        `Includes ${Number(taxSummary.totalMiles).toFixed(0)} business miles ($${Number(taxSummary.mileageDeduction || 0).toFixed(2)} @ $0.67/mi).`,
        14,
        yPos + 4,
      );
      yPos += 10;
    }
    yPos += 10;
  }

  // ---- Profit & Loss
  if (profitLoss) {
    if (yPos > 220) { doc.addPage(); yPos = 20; }
    yPos = addSectionTitle(doc, yPos, 'Profit & Loss');

    const plRows = [
      ['Revenue (Paid Invoices)', `$${revenue.toFixed(2)}`],
      ['Pending Revenue', `$${Number(profitLoss.pendingRevenue || 0).toFixed(2)}`],
      ['Total Expenses', `-$${expenses.toFixed(2)}`],
      ['Mileage Deduction', `-$${mileageDed.toFixed(2)}`],
      ['Net Profit', `$${netProfit.toFixed(2)}`],
      ['Profit Margin', `${Number(profitLoss.profitMargin || 0).toFixed(1)}%`],
    ];

    safeAutoTable(doc, {
      ...getTableStyles(),
      startY: yPos,
      head: [['Line Item', 'Amount']],
      body: plRows,
      columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
      didParseCell: (data) => {
        // Emphasise the Net Profit row.
        if (data.row.index === 4 && data.section === 'body') {
          data.cell.styles.fillColor = COLORS.lightGray;
          data.cell.styles.textColor = netProfit >= 0 ? COLORS.success : COLORS.danger;
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
    yPos = doc.lastAutoTable.finalY + 15;
  }

  // ---- Quarterly Estimates
  if (quarterlyEstimates?.quarters?.length) {
    if (yPos > 220) { doc.addPage(); yPos = 20; }
    yPos = addSectionTitle(doc, yPos, 'Quarterly Estimated Payments');

    const qRows = quarterlyEstimates.quarters.map((q) => [
      q.name,
      `${q.start} to ${q.end}`,
      `Due ${q.due}`,
      `$${Number(q.revenue || 0).toFixed(2)}`,
      `$${Number(q.expenses || 0).toFixed(2)}`,
      `$${Number(q.netIncome || 0).toFixed(2)}`,
      `$${Number(q.totalEstimatedTax || 0).toFixed(2)}`,
    ]);

    safeAutoTable(doc, {
      ...getTableStyles(),
      startY: yPos,
      head: [['Qtr', 'Period', 'Due', 'Revenue', 'Expenses', 'Net Income', 'Est. Tax']],
      body: qRows,
      foot: [[
        'YEAR',
        '',
        '',
        '',
        '',
        `$${Number(quarterlyEstimates.annualNetIncome || 0).toFixed(2)}`,
        `$${Number(quarterlyEstimates.totalEstimatedTax || 0).toFixed(2)}`,
      ]],
      footStyles: { fillColor: COLORS.lightGray, textColor: COLORS.black, fontStyle: 'bold' },
      columnStyles: {
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right', fontStyle: 'bold' },
      },
    });
    yPos = doc.lastAutoTable.finalY + 6;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...COLORS.gray);
    doc.text(
      'Estimates use 15.3% self-employment tax + 22% federal bracket. Confirm with your CPA before filing.',
      14,
      yPos + 4,
    );
  }

  addProfessionalFooter(doc);
  const stamp = new Date().toISOString().split('T')[0];
  doc.save(`Tax_Report_${stamp}.pdf`);
};

// ==================== INVOICE PDF ====================
export const generateInvoicePDF = (invoice, companyInfo = {}, options = {}) => {
  // Doc-type awareness: pass `_docType: 'estimate'` on the invoice OR
  // `options.docType = 'estimate'` to generate an estimate-flavored PDF
  // (title / meta labels / totals row wording). Everything else is shared.
  const isEstimate = (invoice?._docType === 'estimate') || options.docType === 'estimate';
  const docLabel = isEstimate ? 'Estimate' : 'Invoice';
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Bright accent color matching in-app preview (Paradise-inspired reference).
  const ACCENT = [36, 156, 219];

  // Top accent bar
  doc.setFillColor(...ACCENT);
  doc.rect(0, 0, pageWidth, 5, 'F');

  // Company logo top-left (if provided) — sized prominently so it's the
  // first thing the customer sees, just like the Paradise reference.
  let headerLeftX = 14;
  if (companyInfo.logo) {
    try {
      const fmt = /^data:image\/(\w+);/i.exec(companyInfo.logo)?.[1]?.toUpperCase() || 'PNG';
      // ~32mm square in the top-left, aligned with the accent bar.
      doc.addImage(companyInfo.logo, fmt, 14, 10, 32, 32);
      headerLeftX = 50;
    } catch (e) {
      console.warn('[PDFGenerator] Failed to render company logo:', e);
    }
  }

  // Company info block (shifts right when the logo is present)
  doc.setTextColor(...COLORS.black);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(companyInfo.name || 'Maidly Business Manager', headerLeftX, 18);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkGray);
  let leftY = 24;
  if (companyInfo.address) {
    const wrapped = doc.splitTextToSize(companyInfo.address, 75);
    doc.text(wrapped, headerLeftX, leftY);
    leftY += wrapped.length * 4;
  }
  if (companyInfo.phone) { doc.text(companyInfo.phone, headerLeftX, leftY); leftY += 4; }
  if (companyInfo.email) {
    doc.setTextColor(...ACCENT);
    doc.text(companyInfo.email, headerLeftX, leftY);
    doc.setTextColor(...COLORS.darkGray);
  }

  // Decorative "Invoice" box + meta block on the right
  const rightBlockX = pageWidth - 82;
  doc.setDrawColor(...ACCENT);
  doc.setLineWidth(0.6);
  doc.rect(rightBlockX, 12, 34, 12);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.black);
  doc.text(docLabel, rightBlockX + 17, 20, { align: 'center' });

  // Right meta rows
  const invoiceIssueDate = invoice.date || invoice.createdAt || invoice.issueDate;
  const totalPaid = (invoice.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
  const balanceDue = Math.max(0, Number(invoice.total || 0) - totalPaid);

  const metaRows = [
    [`${docLabel} Number:`, invoice.estimateNumber || invoice.invoiceNumber || 'N/A'],
    ...(invoice.poNumber ? [['P.O./S.O. #:', invoice.poNumber]] : []),
    [`${docLabel} Date:`, formatLocaleDate(invoiceIssueDate)],
    [isEstimate ? 'Valid Until:' : 'Payment Due:', formatLocaleDate(invoice.dueDate)],
  ];
  doc.setFontSize(9);
  let metaY = 30;
  metaRows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    doc.text(label, rightBlockX, metaY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.black);
    doc.text(String(value), pageWidth - 14, metaY, { align: 'right' });
    metaY += 5;
  });
  // Amount Due (USD) — emphasized
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text(isEstimate ? 'Total (USD):' : 'Amount Due (USD):', rightBlockX, metaY + 3);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.black);
  doc.text(`$${(isEstimate ? Number(invoice.total || 0) : balanceDue).toFixed(2)}`, pageWidth - 14, metaY + 3, { align: 'right' });

  // Divider under header
  const yAfterHeader = Math.max(leftY, metaY + 8) + 6;
  doc.setDrawColor(...COLORS.borderGray);
  doc.setLineWidth(0.4);
  doc.line(14, yAfterHeader, pageWidth - 14, yAfterHeader);

  let yPos = yAfterHeader + 8;

  // BILL TO
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.gray);
  doc.text('BILL TO', 14, yPos);
  yPos += 5;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.black);
  doc.text(invoice.customerName || 'Customer', 14, yPos);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkGray);
  let billY = yPos + 5;
  // Primary contact person, on its own line under the business name.
  if (invoice.customerContactName) {
    doc.text(String(invoice.customerContactName), 14, billY);
    billY += 4;
  }
  if (invoice.customerAddress) {
    const wrapped = doc.splitTextToSize(invoice.customerAddress, 100);
    doc.text(wrapped, 14, billY);
    billY += wrapped.length * 4;
  }
  if (invoice.customerEmail) {
    doc.setTextColor(...ACCENT);
    doc.text(invoice.customerEmail, 14, billY);
    doc.setTextColor(...COLORS.darkGray);
    billY += 4;
  }
  if (invoice.customerPhone) {
    doc.text(invoice.customerPhone, 14, billY);
    billY += 4;
  }

  yPos = billY + 6;

  // Items table
  const itemsData = (invoice.items || []).map((item) => [
    item.description || 'Service',
    item.quantity || 1,
    `$${(item.rate || 0).toFixed(2)}`,
    `$${(item.amount || item.rate * item.quantity || 0).toFixed(2)}`
  ]);
  safeAutoTable(doc, {
    startY: yPos,
    head: [['Items', 'Quantity', 'Price', 'Amount']],
    body: itemsData,
    theme: 'plain',
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: COLORS.gray,
      fontStyle: 'bold',
      fontSize: 8.5,
      lineWidth: { bottom: 0.6 },
      lineColor: [148, 163, 184],
    },
    bodyStyles: {
      fontSize: 9.5,
      textColor: COLORS.black,
      lineWidth: { bottom: 0.2 },
      lineColor: COLORS.borderGray,
      cellPadding: { top: 3, bottom: 3, left: 2, right: 2 },
    },
    columnStyles: {
      0: { cellWidth: 'auto' },
      1: { cellWidth: 22, halign: 'center' },
      2: { cellWidth: 28, halign: 'right' },
      3: { cellWidth: 32, halign: 'right', fontStyle: 'bold' }
    },
    margin: { left: 14, right: 14 },
  });
  yPos = doc.lastAutoTable.finalY + 6;

  // Totals — right-aligned stack
  const totalsRight = pageWidth - 14;
  const totalsLeftLabelX = pageWidth - 75;
  const taxRate = Number(invoice.taxRate ?? 0);
  const taxLabel = `Sales Tax (${taxRate % 1 === 0 ? taxRate.toFixed(0) : taxRate.toFixed(3)}%):`;

  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.darkGray);
  doc.text('Subtotal:', totalsLeftLabelX, yPos);
  doc.text(`$${(invoice.subtotal || invoice.total || 0).toFixed(2)}`, totalsRight, yPos, { align: 'right' });
  yPos += 6;
  doc.text(taxLabel, totalsLeftLabelX, yPos);
  doc.text(`$${(invoice.tax || 0).toFixed(2)}`, totalsRight, yPos, { align: 'right' });
  yPos += 4;
  // Line
  doc.setDrawColor(...COLORS.borderGray);
  doc.setLineWidth(0.4);
  doc.line(totalsLeftLabelX, yPos, totalsRight, yPos);
  yPos += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...COLORS.black);
  doc.text('Total:', totalsLeftLabelX, yPos);
  doc.text(`$${(invoice.total || 0).toFixed(2)}`, totalsRight, yPos, { align: 'right' });
  yPos += 4;
  doc.setDrawColor(...ACCENT);
  doc.setLineWidth(1);
  doc.line(totalsLeftLabelX, yPos, totalsRight, yPos);
  yPos += 6;

  if (totalPaid > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.success);
    doc.text('Amount Paid:', totalsLeftLabelX, yPos);
    doc.text(`-$${totalPaid.toFixed(2)}`, totalsRight, yPos, { align: 'right' });
    yPos += 6;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  if (balanceDue > 0.005) {
    doc.setTextColor(...COLORS.black);
    doc.text('Amount Due (USD):', totalsLeftLabelX, yPos);
    doc.text(`$${balanceDue.toFixed(2)}`, totalsRight, yPos, { align: 'right' });
  } else if (totalPaid > 0) {
    doc.setTextColor(...COLORS.success);
    doc.text('PAID IN FULL', totalsLeftLabelX, yPos);
    doc.text(`$0.00`, totalsRight, yPos, { align: 'right' });
  } else {
    doc.setTextColor(...COLORS.black);
    doc.text('Amount Due (USD):', totalsLeftLabelX, yPos);
    doc.text(`$${(invoice.total || 0).toFixed(2)}`, totalsRight, yPos, { align: 'right' });
  }
  yPos += 12;

  // Notes
  if (invoice.notes) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.gray);
    doc.text('NOTES', 14, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.darkGray);
    const wrapped = doc.splitTextToSize(invoice.notes, pageWidth - 28);
    doc.text(wrapped, 14, yPos + 5);
    yPos += 5 + wrapped.length * 4;
  }
  if (invoice.terms) {
    yPos += 4;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.gray);
    doc.text('PAYMENT TERMS', 14, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.darkGray);
    const wrapped = doc.splitTextToSize(invoice.terms, pageWidth - 28);
    doc.text(wrapped, 14, yPos + 5);
  }

  // Thank you — footer (per-invoice override → company preset → default)
  const footerNote =
    invoice.footerNote
    || companyInfo.invoiceFooterNote
    || 'Thank you for supporting my small business!';
  doc.setFontSize(10);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.gray);
  doc.text(footerNote, pageWidth / 2, pageHeight - 20, { align: 'center', maxWidth: pageWidth - 40 });

  // Bottom accent line
  doc.setDrawColor(...ACCENT);
  doc.setLineWidth(1.2);
  doc.line(0, pageHeight - 8, pageWidth, pageHeight - 8);

  // ===== Status stamp =====
  // PAID → embed the branded PNG stamp the user uploaded (same asset used by
  // the on-screen preview). Everything else falls back to the light-tone
  // text-based double-border stamp so PENDING / OVERDUE / PARTIAL / DRAFT
  // still read at a glance. Estimates use their own status vocabulary
  // (draft / sent / accepted / rejected / converted) and never carry the
  // PAID image.
  (() => {
    if (isEstimate) {
      const stampMap = {
        draft:     ['DRAFT',     [203, 213, 225]],
        sent:      ['SENT',      [147, 197, 253]],
        accepted:  ['ACCEPTED',  [134, 239, 172]],
        rejected:  ['REJECTED',  [252, 165, 165]],
        converted: ['CONVERTED', [196, 181, 253]],
      };
      const [stampLabel, stampLight] = stampMap[invoice.status] || stampMap.draft;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(48);
      doc.setTextColor(...stampLight);
      const textWidth = doc.getTextWidth(stampLabel);
      const cx = pageWidth * 0.62;
      const cy = pageHeight * 0.7;
      const angleDeg = -18;
      const angleRad = (angleDeg * Math.PI) / 180;
      doc.setDrawColor(...stampLight);
      doc.setLineWidth(0.9);
      const halfW = textWidth / 2 + 8;
      const halfH = 11;
      const cos = Math.cos(angleRad);
      const sin = Math.sin(angleRad);
      const rotate = (x, y) => [cx + x * cos - y * sin, cy + x * sin + y * cos];
      const drawRect = (hw, hh) => {
        const corners = [rotate(-hw, -hh), rotate(hw, -hh), rotate(hw, hh), rotate(-hw, hh)];
        for (let i = 0; i < 4; i++) {
          const [ax, ay] = corners[i];
          const [bx, by] = corners[(i + 1) % 4];
          doc.line(ax, ay, bx, by);
        }
      };
      drawRect(halfW, halfH);
      drawRect(halfW - 1.6, halfH - 1.6);
      doc.text(stampLabel, cx, cy + 4, { align: 'center', angle: -angleDeg });
      doc.setTextColor(0, 0, 0);
      return;
    }
    const totalPaid = (invoice.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
    const totalAmount = Number(invoice.total || 0);
    const balanceLeft = Math.max(0, totalAmount - totalPaid);
    const isFullyPaid = invoice.status === 'paid' || (totalAmount > 0 && balanceLeft <= 0.005);
    const isPartial = !isFullyPaid && totalPaid > 0;
    const due = invoice.dueDate ? new Date(invoice.dueDate) : null;
    const isOverdue = !isFullyPaid && due && due < new Date();

    if (isFullyPaid) {
      // Native aspect of the trimmed PNG is 632×387 ≈ 1.63.
      const stampW = 82; // mm
      const stampH = stampW * (387 / 632);
      const cx = pageWidth * 0.58;
      const cy = pageHeight * 0.55;
      try {
        doc.addImage(
          PAID_STAMP_PNG_BASE64,
          'PNG',
          cx - stampW / 2,
          cy - stampH / 2,
          stampW,
          stampH,
        );
      } catch (e) {
        // If image embedding fails for any reason, don't block the PDF —
        // fall back to a red text stamp so the invoice still reads as PAID.
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(48);
        doc.setTextColor(220, 38, 38);
        doc.text('PAID', cx, cy, { align: 'center', angle: 18 });
        doc.setTextColor(0, 0, 0);
      }
      return;
    }

    // Non-paid statuses: draw the double-border text stamp (unchanged look).
    let stampLabel = null;
    let stampLight = null;
    if (isOverdue)                       { stampLabel = 'OVERDUE'; stampLight = [252, 165, 165]; }
    else if (isPartial)                  { stampLabel = 'PARTIAL'; stampLight = [253, 186, 116]; }
    else if (invoice.status === 'draft') { stampLabel = 'DRAFT';   stampLight = [203, 213, 225]; }
    else                                 { stampLabel = 'PENDING'; stampLight = [252, 211, 77]; }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(48);
    doc.setTextColor(...stampLight);
    const textWidth = doc.getTextWidth(stampLabel);

    const cx = pageWidth * 0.62;
    const cy = pageHeight * 0.7;
    const angleDeg = -18;
    const angleRad = (angleDeg * Math.PI) / 180;

    doc.setDrawColor(...stampLight);
    doc.setLineWidth(0.9);
    const halfW = textWidth / 2 + 8;
    const halfH = 11;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);
    const rotate = (x, y) => [cx + x * cos - y * sin, cy + x * sin + y * cos];
    const drawRect = (hw, hh) => {
      const corners = [
        rotate(-hw, -hh), rotate(hw, -hh),
        rotate(hw,  hh),  rotate(-hw, hh),
      ];
      for (let i = 0; i < 4; i++) {
        const [ax, ay] = corners[i];
        const [bx, by] = corners[(i + 1) % 4];
        doc.line(ax, ay, bx, by);
      }
    };
    drawRect(halfW, halfH);
    drawRect(halfW - 1.6, halfH - 1.6);

    doc.text(stampLabel, cx, cy + 4, { align: 'center', angle: -angleDeg });
    doc.setTextColor(0, 0, 0);
  })();

  // Return the PDF as base64 (without the data:...;base64, prefix) when
  // the caller asks for it — used by the "email invoice" flow to attach
  // the PDF directly instead of triggering a download.
  if (options.returnBase64) {
    const dataUri = doc.output('datauristring'); // data:application/pdf;base64,XXXX
    const idx = dataUri.indexOf('base64,');
    return idx >= 0 ? dataUri.slice(idx + 'base64,'.length) : dataUri;
  }

  const outName = isEstimate
    ? `Estimate_${invoice.estimateNumber || 'draft'}.pdf`
    : `Invoice_${invoice.invoiceNumber || 'draft'}.pdf`;
  doc.save(outName);
};

// ==================== MILEAGE REPORT ====================
export const generateMileageReportPDF = (trips, summary = {}) => {
  const doc = new jsPDF();

  let yPos = addProfessionalHeader(doc, 'Mileage Report', 'Vehicle & Travel Tracking');

  const totalMiles = trips.reduce((sum, t) => sum + (t.distance || 0), 0);
  const totalTrips = trips.length;
  const ratePerMile = summary.ratePerMile || 0.67;
  const totalValue = totalMiles * ratePerMile;

  yPos = layoutSummaryRow(doc, yPos, [
    { label: 'Total Miles', value: totalMiles.toFixed(1),         color: COLORS.info },
    { label: 'Total Trips', value: totalTrips,                    color: COLORS.primaryLight },
    { label: 'Rate/Mile',   value: `$${ratePerMile.toFixed(2)}`,  color: COLORS.warning },
    { label: 'Total Value', value: `$${totalValue.toFixed(2)}`,   color: COLORS.success },
  ]);

  // Trips table
  yPos = addSectionTitle(doc, yPos, 'Trip Details');

  const tripData = trips.map(trip => [
    formatLocaleDate(trip.date),
    trip.purpose || 'Business',
    trip.startLocation || 'N/A',
    trip.endLocation || 'N/A',
    `${(trip.distance || 0).toFixed(1)} mi`,
    trip.notes || '-'
  ]);

  safeAutoTable(doc, {
    ...getTableStyles(),
    startY: yPos,
    head: [['Date', 'Purpose', 'From', 'To', 'Distance', 'Notes']],
    body: tripData,
    columnStyles: {
      4: { halign: 'right', fontStyle: 'bold' }
    }
  });

  addProfessionalFooter(doc);
  doc.save(`Mileage_Report_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ==================== TIME TRACKING REPORT ====================
export const generateTimeReportPDF = (entries, summary = {}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  let yPos = addProfessionalHeader(doc, 'Time Tracking Report', 'Employee Hours & Attendance');

  const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);
  const totalEntries = entries.length;
  const avgHours = totalEntries > 0 ? totalHours / totalEntries : 0;

  yPos = layoutSummaryRow(doc, yPos, [
    { label: 'Total Hours', value: totalHours.toFixed(1), color: COLORS.info },
    { label: 'Entries',     value: totalEntries,          color: COLORS.primaryLight },
    { label: 'Avg Hours',   value: avgHours.toFixed(1),   color: COLORS.success },
  ]);

  // Entries table
  yPos = addSectionTitle(doc, yPos, 'Time Entries');

  const entryData = entries.map(entry => [
    formatLocaleDate(entry.date),
    entry.employeeName || 'N/A',
    entry.clockIn || 'N/A',
    entry.clockOut || 'N/A',
    `${(entry.hours || 0).toFixed(1)} hrs`,
    entry.notes || '-'
  ]);

  safeAutoTable(doc, {
    ...getTableStyles(),
    startY: yPos,
    head: [['Date', 'Employee', 'Clock In', 'Clock Out', 'Hours', 'Notes']],
    body: entryData,
    columnStyles: {
      4: { halign: 'right', fontStyle: 'bold' }
    }
  });

  addProfessionalFooter(doc);
  doc.save(`Time_Report_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ==================== CUSTOMER REPORT ====================
export const generateCustomerReportPDF = (customers) => {
  const doc = new jsPDF();

  let yPos = addProfessionalHeader(doc, 'Customer Directory', `${customers.length} Total Customers`);

  const activeCustomers = customers.filter(c => c.status === 'active').length;

  yPos = layoutSummaryRow(doc, yPos, [
    { label: 'Total Customers', value: customers.length,                     color: COLORS.info },
    { label: 'Active',          value: activeCustomers,                      color: COLORS.success },
    { label: 'Inactive',        value: customers.length - activeCustomers,   color: COLORS.gray },
  ]);

  // Customers table
  yPos = addSectionTitle(doc, yPos, 'Customer List');

  const customerData = customers.map(customer => [
    customer.name || 'N/A',
    customer.email || 'N/A',
    customer.phone || 'N/A',
    customer.address || 'N/A'
  ]);

  safeAutoTable(doc, {
    ...getTableStyles(),
    startY: yPos,
    head: [['Name', 'Email', 'Phone', 'Address']],
    body: customerData,
  });

  addProfessionalFooter(doc);
  doc.save(`Customer_Directory_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ==================== INVENTORY REPORT ====================
export const generateInventoryReportPDF = (items, summary = {}) => {
  const doc = new jsPDF();

  let yPos = addProfessionalHeader(doc, 'Inventory Report', 'Stock & Supplies Management');

  const totalItems = items.length;
  const totalValue = items.reduce((sum, i) => sum + ((i.quantity || 0) * (i.unitPrice || 0)), 0);
  const lowStock = items.filter(i => (i.quantity || 0) <= (i.minStock || 5)).length;

  yPos = layoutSummaryRow(doc, yPos, [
    { label: 'Total Items', value: totalItems,                     color: COLORS.info },
    { label: 'Total Value', value: `$${totalValue.toFixed(2)}`,    color: COLORS.primaryLight },
    { label: 'Low Stock',   value: lowStock,                       color: lowStock > 0 ? COLORS.danger : COLORS.success },
  ]);
  
  // Inventory table
  yPos = addSectionTitle(doc, yPos, 'Inventory Items');
  
  const itemData = items.map(item => [
    item.name || 'N/A',
    item.category || 'N/A',
    item.quantity || 0,
    `$${(item.unitPrice || 0).toFixed(2)}`,
    `$${((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}`,
    (item.quantity || 0) <= (item.minStock || 5) ? 'LOW' : 'OK'
  ]);
  
  safeAutoTable(doc, {
    ...getTableStyles(),
    startY: yPos,
    head: [['Item', 'Category', 'Qty', 'Unit Price', 'Total', 'Status']],
    body: itemData,
    columnStyles: {
      2: { halign: 'center' },
      3: { halign: 'right' },
      4: { halign: 'right', fontStyle: 'bold' },
      5: { halign: 'center' }
    },
    didParseCell: function(data) {
      if (data.column.index === 5 && data.cell.raw === 'LOW') {
        data.cell.styles.textColor = COLORS.danger;
        data.cell.styles.fontStyle = 'bold';
      }
    }
  });
  
  addProfessionalFooter(doc);
  doc.save(`Inventory_Report_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ==================== SCHEDULE REPORT ====================
export const generateScheduleReportPDF = (appointments, dateRange = {}) => {
  const doc = new jsPDF();

  const subtitle = dateRange.start && dateRange.end
    ? `${dateRange.start} — ${dateRange.end}`
    : 'All Appointments';

  let yPos = addProfessionalHeader(doc, 'Schedule Report', subtitle);

  const totalAppts = appointments.length;
  const completed = appointments.filter(a => a.status === 'completed').length;
  const upcoming = appointments.filter(a => a.status === 'scheduled' || a.status === 'confirmed').length;

  yPos = layoutSummaryRow(doc, yPos, [
    { label: 'Total Appointments', value: totalAppts, color: COLORS.info },
    { label: 'Completed',          value: completed,  color: COLORS.success },
    { label: 'Upcoming',           value: upcoming,   color: COLORS.warning },
  ]);

  // Appointments table
  yPos = addSectionTitle(doc, yPos, 'Appointment Details');

  const apptData = appointments.map(appt => [
    formatLocaleDate(appt.date),
    appt.time || 'N/A',
    appt.customerName || 'N/A',
    appt.address || 'N/A',
    appt.service || 'N/A',
    (appt.status || 'scheduled').toUpperCase()
  ]);
  
  safeAutoTable(doc, {
    ...getTableStyles(),
    startY: yPos,
    head: [['Date', 'Time', 'Customer', 'Address', 'Service', 'Status']],
    body: apptData,
    columnStyles: {
      5: { halign: 'center' }
    },
    didParseCell: function(data) {
      if (data.column.index === 5) {
        const status = data.cell.raw;
        if (status === 'COMPLETED') data.cell.styles.textColor = COLORS.success;
        else if (status === 'CANCELLED') data.cell.styles.textColor = COLORS.danger;
        else data.cell.styles.textColor = COLORS.accent;
        data.cell.styles.fontStyle = 'bold';
      }
    }
  });
  
  addProfessionalFooter(doc);
  doc.save(`Schedule_Report_${new Date().toISOString().split('T')[0]}.pdf`);
};

// ==================== CLEANING INSPECTION REPORT ====================
export const generateInspectionReportPDF = (inspection, checklist, companyInfo = {}) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Calculate score
  let totalItems = 0;
  let passedItems = 0;
  
  Object.keys(checklist).forEach(cat => {
    checklist[cat].items.forEach(item => {
      const result = inspection.checklist?.[cat]?.[item.id];
      if (result?.status && result.status !== 'na') {
        totalItems++;
        if (result.status === 'pass') passedItems++;
      }
    });
  });
  
  const score = totalItems > 0 ? Math.round((passedItems / totalItems) * 100) : 0;
  const scoreColor = score >= 80 ? COLORS.success : score >= 60 ? COLORS.accent : COLORS.danger;
  
  let yPos = addProfessionalHeader(doc, 'Inspection Report', inspection.customerName || 'Cleaning Inspection');

  yPos = layoutSummaryRow(doc, yPos, [
    { label: 'Score',       value: `${score}%`,            color: scoreColor },
    { label: 'Passed',      value: passedItems,            color: COLORS.success },
    { label: 'Failed',      value: totalItems - passedItems, color: COLORS.danger },
    { label: 'Total Items', value: totalItems,             color: COLORS.gray },
  ]);

  // Inspection details
  doc.setFillColor(...COLORS.lightGray);
  doc.roundedRect(14, yPos, pageWidth - 28, 35, 2, 2, 'F');

  yPos += 8;
  yPos = addInfoRow(doc, yPos, 'Address', inspection.address, pageWidth);
  yPos = addInfoRow(doc, yPos, 'Date', formatLocaleDate(inspection.scheduledDate), pageWidth);
  yPos = addInfoRow(doc, yPos, 'Inspector', inspection.inspectorName || 'N/A', pageWidth);

  yPos += 15;
  
  // Checklist results
  Object.entries(checklist).forEach(([categoryKey, category]) => {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }
    
    yPos = addSectionTitle(doc, yPos, category.label);
    
    const items = category.items.map(item => {
      const result = inspection.checklist?.[categoryKey]?.[item.id];
      let status = 'Not Checked';
      let statusColor = COLORS.gray;
      
      if (result?.status === 'pass') {
        status = 'PASS';
        statusColor = COLORS.success;
      } else if (result?.status === 'fail') {
        status = 'FAIL';
        statusColor = COLORS.danger;
      } else if (result?.status === 'na') {
        status = 'N/A';
        statusColor = COLORS.gray;
      }
      
      return [
        item.name,
        item.critical ? 'Yes' : 'No',
        status,
        result?.notes || '-'
      ];
    });
    
    safeAutoTable(doc, {
      ...getTableStyles(),
      startY: yPos,
      head: [['Item', 'Critical', 'Status', 'Notes']],
      body: items,
      columnStyles: {
        1: { halign: 'center', cellWidth: 25 },
        2: { halign: 'center', cellWidth: 25 },
        3: { cellWidth: 50 }
      },
      didParseCell: function(data) {
        if (data.column.index === 2) {
          const status = data.cell.raw;
          if (status === 'PASS') data.cell.styles.textColor = COLORS.success;
          else if (status === 'FAIL') data.cell.styles.textColor = COLORS.danger;
          else data.cell.styles.textColor = COLORS.gray;
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
  });
  
  addProfessionalFooter(doc, companyInfo.name || 'Maidly Business Manager');
  doc.save(`Inspection_Report_${inspection.id || 'draft'}.pdf`);
};

export default {
  generateExpenseReportPDF,
  generateTaxReportPDF,
  generateInvoicePDF,
  generateMileageReportPDF,
  generateTimeReportPDF,
  generateCustomerReportPDF,
  generateInventoryReportPDF,
  generateScheduleReportPDF,
  generateInspectionReportPDF
};
